/**
 * Expenses — what the year costs the client, as a pure function.
 *
 * Four outflows, each with a rule of its own:
 *
 *   - LIVING EXPENSES: the client's cost of living, which a strategy scenario may override
 *     (`scenarioLivingExpenses`) to model spending differently without touching the fact-find.
 *   - CAPITAL EXPENSES: one-off costs a client has pencilled in against a MONTH (`YYYY-MM`) — a new car,
 *     a bathroom. Only those falling inside the year being assessed count, which is the whole subtlety
 *     here: see {@link sumCapitalExpensesForProjectionPeriod}.
 *   - THE PARTNER'S RENT: where the rent-the-home strategy has the client renting their home out, the
 *     partner's own rent elsewhere is a weekly figure, annualised.
 *   - AGED-CARE COSTS: the annual cost of care, passed in already computed by the caller
 *     (`integratedAgedCareCost`) — the aged-care engine's figure, not one struck here.
 *
 * What this does NOT do is total the year's outgoings. Tax, rental expenses and loan repayments are
 * assessed elsewhere (incomeTax.ts, incomeAssessment.ts, loanRepayments.ts) and `calculateFinancials`
 * adds all of them together with these to strike its cashflow.
 *
 * Extracted from `calculateFinancials`'s "--- 7. Final Cash Flow & Net Worth ---" block, which still
 * calls it — through the injectable {@link ExpenseAssessor} seam, so the projection engine can hand in
 * the assessor owned by its expensesPlugin, the plugin that already owns WHEN these outflows leave the
 * bank.
 */

import { scenarioLivingExpensesOr } from '../../../../../shared/src/calculation/support/livingExpenses.ts';
import { resolveAnchorFromProjectionContext } from './projectionAnchor.ts';
import type { FactFind, FactFindExpenses } from '../../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions } from '../../../../../shared/src/calculation/strategyOptionsTypes.ts';
import { residencyOf, type Residency } from '../../../../../shared/src/calculation/householdPosition.ts';

/** Parse fact-find capital expense `year` field (`YYYY-MM`) to a Date (first of month). */
export function parseCapitalExpenseMonthDate(yearField: unknown): Date | null {
    if (!yearField || typeof yearField !== 'string') return null;
    const m = String(yearField).trim().match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    const y = parseInt(m[1]!, 10);
    const mo = parseInt(m[2]!, 10) - 1;
    if (mo < 0 || mo > 11 || y < 1900 || y > 2200) return null;
    return new Date(y, mo, 1);
}

/**
 * Sum one-off capital expenses whose expected month falls in strategy year `yearIndex` (1-based), or year 1 if omitted.
 *
 * The bands are struck at MONTH granularity — the 1st of the anchor's month, not the anchor's own day —
 * because a capital expense is recorded as `YYYY-MM` and has no day to compare against. Take the anchor's
 * day here and an expense pencilled in for the month the client signs up in is silently dropped: it
 * parses to that month's 1st, which sorts before a mid-month anchor. That is the same leniency
 * {@link sumCapitalExpensesForProjectionPeriod} applies to its own period start, and the two must agree
 * because this is its fallback.
 */
export function sumCapitalExpensesForStrategyYear(expenses: FactFindExpenses | undefined, projectionContext: any = {}): number {
    const anchor = resolveAnchorFromProjectionContext(projectionContext);
    const py = projectionContext.yearIndex;
    const strategyYear = py != null && py > 0 ? py : 1;
    const yearStart = new Date(anchor.getFullYear() + strategyYear - 1, anchor.getMonth(), 1);
    const yearEnd = new Date(anchor.getFullYear() + strategyYear, anchor.getMonth(), 1);
    const list = expenses?.capitalExpenses || [];
    return list.reduce((sum: number, exp) => {
        const d = parseCapitalExpenseMonthDate(exp.year);
        if (!d || Number.isNaN(d.getTime())) return sum;
        if (d >= yearStart && d < yearEnd) return sum + (exp.amount || 0);
        return sum;
    }, 0);
}

/**
 * Sum capital expenses whose `YYYY-MM` month falls inside [capitalExpensePeriodStart, capitalExpensePeriodEnd] (Australian FY row).
 * Falls back to strategy-year bands when period bounds are not set.
 */
export function sumCapitalExpensesForProjectionPeriod(expenses: FactFindExpenses | undefined, projectionContext: any = {}): number {
    const rawStart = projectionContext.capitalExpensePeriodStart;
    const rawEnd = projectionContext.capitalExpensePeriodEnd;
    if (rawStart != null && rawEnd != null) {
        const periodStart = rawStart instanceof Date ? rawStart : new Date(rawStart);
        const periodEnd = rawEnd instanceof Date ? rawEnd : new Date(rawEnd);
        if (!Number.isNaN(periodStart.getTime()) && !Number.isNaN(periodEnd.getTime())) {
            const ps = new Date(periodStart.getFullYear(), periodStart.getMonth(), 1);
            const list = expenses?.capitalExpenses || [];
            return list.reduce((sum: number, exp) => {
                const d = parseCapitalExpenseMonthDate(exp.year);
                if (!d || Number.isNaN(d.getTime())) return sum;
                const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
                if (monthStart.getTime() >= ps.getTime() && monthStart.getTime() <= periodEnd.getTime()) {
                    return sum + (exp.amount || 0);
                }
                return sum;
            }, 0);
        }
    }
    return sumCapitalExpensesForStrategyYear(expenses, projectionContext);
}

/** What the assessment needs: the fact-find's expenses, the strategy's overrides, and which year it is. */
export interface ExpenseAssessmentInput {
    /** Read for `livingExpenses` and `capitalExpenses`. */
    expenses: FactFindExpenses | undefined;
    /**
     * Read for the housing the household pays for (8 Oct 2026): a tenant's `rent.rentAmount`, a
     * village resident's `retirementVillage.ongoingFees`, board paid to family
     * (`livingWithFamily.boardPerWeek`), and each person's residency. Optional for callers that
     * assess expenses alone.
     */
    factFind?: FactFind;
    /** Read for `scenarioLivingExpenses`, `rentHome`/`partnerRent` and `integratedAgedCareCost`. */
    strategyOptions: StrategyOptions;
    /** Which projection year and period this is, for dating the capital expenses. `{}` for a standalone call. */
    projectionContext: any;
}

/** The year's assessed outgoings, each reported separately because the cashflow breakdown names each one. */
export interface ExpenseAssessment {
    /** The client's cost of living for the year — the scenario override where one is set. */
    livingExpenses: number;
    /** One-off costs falling inside this year only. */
    capitalExpenses: number;
    /** The partner's annualised rent, where the rent-the-home strategy applies. 0 otherwise. */
    partnerRent: number;
    /** The annual cost of aged care, as the caller supplied it. */
    integratedAgedCareCost: number;
    /** What the household pays to live where it lives: rent, village fees, board — see {@link calculateHousingCosts}. */
    housingCosts?: number;
    /** "rent", "village fees", "board", or a list of those that apply. */
    housingCostsLabel?: string;
}

/**
 * HOUSING COSTS (owner-caught, 8 Oct 2026: a village resident's weekly fees were recorded on the
 * profile and never reached the cashflow). The living-expenses figure is a cost of living with the
 * home owned (the ASFA benchmarks assume it), so what the household pays to live where it lives is
 * its own line: a tenant's weekly rent, a village resident's ongoing (recurrent) charges, board paid
 * to family where there is no granny-flat interest — each × 52.
 *
 * Charged for the share of the year the arrangement is lived in:
 *   - all of it while someone is still there under the year's options (a partner staying on in the
 *     village keeps the fees going after the other enters care);
 *   - the part before entry where the only resident moved into care this year (the projection
 *     hands the first entrant's share of the year in care as `careFrac`);
 *   - none in a normalised in-care year, where no `careFrac` is given.
 * A departing village resident's charges until the unit is relicensed are not modelled (F.43).
 * CPI-escalated in a projection by `housingEscalationFactor`, as living expenses are.
 */
export function calculateHousingCosts(factFind: FactFind | undefined, strategyOptions: StrategyOptions, projectionContext: any): { housingCosts: number; housingCostsLabel: string } {
    if (!factFind) return { housingCosts: 0, housingCostsLabel: '' };
    const people: Array<'client' | 'partner'> = factFind.adviceFor === 'couple' ? ['client', 'partner'] : ['client'];
    const careFrac = Number(projectionContext?.careFrac);
    const shareOfYear = (kind: Residency): number => {
        if (people.some((p) => residencyOf(factFind, p, strategyOptions) === kind)) return 1;
        const leftForCare = people.some((p) => residencyOf(factFind, p, {}) === kind && residencyOf(factFind, p, strategyOptions) === 'aged_care');
        if (!leftForCare) return 0;
        return Number.isFinite(careFrac) ? Math.max(0, Math.min(1, 1 - careFrac)) : 0;
    };
    const escalation = Number(projectionContext?.housingEscalationFactor) > 0 ? Number(projectionContext.housingEscalationFactor) : 1;
    const rent = (Number(factFind.rent?.rentAmount) || 0) * 52 * shareOfYear('renting');
    const village = (Number(factFind.retirementVillage?.ongoingFees) || 0) * 52 * shareOfYear('retirement_village');
    const board = (Number(factFind.livingWithFamily?.boardPerWeek) || 0) * 52 * shareOfYear('with_family');
    const kinds = [rent > 0 ? 'rent' : '', village > 0 ? 'village fees' : '', board > 0 ? 'board' : ''].filter(Boolean);
    return { housingCosts: (rent + village + board) * escalation, housingCostsLabel: kinds.join(', ') };
}

/**
 * Assesses the year's expenses. Injected into `calculateFinancials` so a caller can own the rules — see
 * {@link calculateExpenses}, the default, and the projection engine's expensesPlugin.
 */
export type ExpenseAssessor = (input: ExpenseAssessmentInput) => ExpenseAssessment;

/**
 * Works out the four outflows for the year.
 *
 * `scenarioLivingExpenses` wins over the fact-find figure whenever it was ENTERED, $0 included — the
 * same rule the projection's expensesPlugin applies (livingExpenses.ts; owner, 9 Oct 2026). It used to
 * win only when truthy, so a $0 entered for life in care fell back to the fact-find in the first year
 * while the projection ran on $0.
 */
export const calculateExpenses: ExpenseAssessor = ({ expenses, factFind, strategyOptions, projectionContext }: ExpenseAssessmentInput): ExpenseAssessment => {
    const livingExpenses = scenarioLivingExpensesOr(strategyOptions.scenarioLivingExpenses, expenses?.livingExpenses);
    const { housingCosts, housingCostsLabel } = calculateHousingCosts(factFind, strategyOptions, projectionContext);

    const capitalExpenses = sumCapitalExpensesForProjectionPeriod(expenses, projectionContext);

    let partnerRent = 0;
    if (strategyOptions.rentHome && strategyOptions.partnerRent) {
        partnerRent = strategyOptions.partnerRent * 52;
    }

    const integratedAgedCareCost = strategyOptions.integratedAgedCareCost || 0;

    return { livingExpenses, capitalExpenses, partnerRent, integratedAgedCareCost, housingCosts, housingCostsLabel };
};
